import { useCallback, useEffect, useState } from 'react';
import {
  dropCandidateFromPending,
  type PendingMaxBumps,
} from '@jackedlogs/shared';

// Shared MAX-bump candidate state container, lifted from mobile's
// apps/mobile/src/maxes/useMaxBumpCandidates.ts so web-rnw's
// useMaxBumpFlowWeb reads the SAME TTL-clear + accept logic instead of a
// second copy (the legacy apps/web has its own pre-lift twin, frozen —
// not touched). The shared data transitions live in
// @jackedlogs/shared/maxBumpCandidates (computeMaxBumpCandidates,
// dropCandidateFromPending); this hook is just the React state container
// + the setTimeout-based 24h auto-clear.
//
// This hook does NOT own the compute call: each platform's useMaxBumpFlow
// computes the candidates (so it can apply the "bad day" snooze filter for
// the bidirectional MAX prompt, next/coaching.md §9) and pushes the result
// in via `setPending`. The TTL auto-clear + per-candidate accept stay here.

// Re-exported for back-compat with existing call sites.
export type { PendingMaxBumps };

export const useMaxBumpCandidates = (): {
  pending: PendingMaxBumps | null;
  setPending: (next: PendingMaxBumps | null) => void;
  acceptCandidate: (exerciseId: string) => void;
  clear: () => void;
} => {
  const [pending, setPending] = useState<PendingMaxBumps | null>(null);

  useEffect(() => {
    if (!pending) return undefined;
    const remaining = pending.expiresAt - Date.now();
    if (remaining <= 0) {
      setPending(null);
      return undefined;
    }
    const handle = setTimeout(() => setPending(null), remaining);
    return () => clearTimeout(handle);
  }, [pending]);

  const acceptCandidate = useCallback((exerciseId: string) => {
    setPending((prev) => dropCandidateFromPending(prev, exerciseId));
  }, []);

  const clear = useCallback(() => setPending(null), []);

  return { pending, setPending, acceptCandidate, clear };
};
